import { fitImageOnPage } from '@/lib/imagePageFit';

/**
 * Maten van de vellen waarop één blok wordt verdeeld. Alle waarden zijn in
 * layoutpixels vanaf de bovenkant van het blok of van een vel.
 */
export interface SheetMetrics {
  /** Afstand tussen de bovenkanten van twee vellen: vel plus tussenruimte op het scherm. */
  pitch: number;
  /** Hoogte van één fysiek A4-vel. */
  sheetHeight: number;
  /** Onderkant van het tekstvlak, gemeten vanaf de bovenkant van een vel. */
  bodyBottom: number;
  /** Bovenkant van de inhoud op een vervolgvel (onder de kop met logo's). */
  continuationTop: number;
}

export interface SheetUnit {
  /** Bovenkant zonder eerder toegepaste verschuivingen. */
  top: number;
  height: number;
  /** Kop of tabelkop: niet als laatste regel onderaan een vel laten staan. */
  keepWithNext?: boolean;
  /** Afbeelding: mag kleiner worden om nog op het vel te passen. */
  shrinkable?: boolean;
}

export interface UnitPlacement {
  /** Extra ruimte boven de unit, zodat hij op het volgende vel begint. */
  push: number;
  /** Nieuwe hoogte van een verkleinde afbeelding. */
  shrinkTo?: number;
}

export interface SheetPlan {
  placements: UnitPlacement[];
  /** Hoogte van het blok na verschuivingen, zonder afronding op hele vellen. */
  contentHeight: number;
  sheetCount: number;
  /** Hoogte die het blok krijgt: precies `sheetCount` vellen. */
  blockHeight: number;
}

/** Subpixels uit de layout mogen geen extra vel of verschuiving veroorzaken. */
const EPSILON = 0.5;

export function sheetIndexAt(y: number, metrics: SheetMetrics): number {
  return Math.max(0, Math.floor((y + EPSILON) / metrics.pitch));
}

export function sheetCountFor(height: number, metrics: SheetMetrics): number {
  if (!Number.isFinite(height) || height <= 0) return 1;
  return Math.max(1, sheetIndexAt(height - 2 * EPSILON, metrics) + 1);
}

export function blockHeightFor(sheetCount: number, metrics: SheetMetrics): number {
  return (sheetCount - 1) * metrics.pitch + metrics.sheetHeight;
}

/**
 * Verdeelt de units van één blok over vellen. Een unit die over de onderkant
 * van het tekstvlak loopt, begint op het volgende vel; een afbeelding wordt
 * eerst kleiner als dat leesbaar blijft. Units die bovenaan een vel al niet
 * passen, blijven staan: verschuiven levert dan alleen een leeg vel op.
 *
 * Dezelfde berekening geldt voor de webweergave en de PDF. Alleen `pitch`
 * verschilt (tussenruimte op het scherm), zodat elk vel dezelfde inhoud krijgt.
 */
export function planSheets(
  units: SheetUnit[],
  blockContentHeight: number,
  metrics: SheetMetrics,
): SheetPlan {
  const placements: UnitPlacement[] = units.map(() => ({ push: 0 }));
  const shiftAt: number[] = [];
  const pushed = new Set<number>();
  let shift = 0;
  let sheet = -1;
  let firstOnSheet = 0;
  let index = 0;
  let iterations = 0;

  while (index < units.length && iterations < units.length * 6) {
    iterations += 1;
    shiftAt[index] = shift;
    const unit = units[index];
    const placement = placements[index];
    const top = unit.top + shift + placement.push;
    const height = placement.shrinkTo ?? unit.height;
    const unitSheet = sheetIndexAt(top, metrics);
    if (unitSheet !== sheet) {
      sheet = unitSheet;
      firstOnSheet = index;
    }
    const sheetTop = unitSheet * metrics.pitch;
    const limit = sheetTop + metrics.bodyBottom;

    // Na een te grote unit kan de volgende in de kop van het vel beginnen.
    const bodyTop = sheetTop + metrics.continuationTop;
    if (unitSheet > 0 && index === firstOnSheet && top < bodyTop - EPSILON && !pushed.has(index)) {
      placement.push += bodyTop - top;
      pushed.add(index);
      continue;
    }

    const advance = () => {
      shift += placement.push + (height - unit.height);
      index += 1;
    };

    if (top + height <= limit + EPSILON) {
      advance();
      continue;
    }

    if (unit.shrinkable && placement.shrinkTo === undefined) {
      const available = limit - top;
      const fit = fitImageOnPage(height, available);
      if (fit.kind === 'shrink' || (index === firstOnSheet && available > 0)) {
        placement.shrinkTo = Math.max(1, Math.floor(available));
        continue;
      }
    }

    // Een afbeelding mag altijd door: bovenaan het volgende vel wordt hij zo nodig kleiner.
    const fitsOnNextSheet =
      unit.shrinkable || height <= metrics.bodyBottom - metrics.continuationTop + EPSILON;
    if (index === firstOnSheet || pushed.has(index) || !fitsOnNextSheet) {
      advance();
      continue;
    }

    let start = index;
    while (start - 1 > firstOnSheet && units[start - 1].keepWithNext && !pushed.has(start - 1)) {
      start -= 1;
    }
    const startTop = units[start].top + shiftAt[start] + placements[start].push;
    const target = (unitSheet + 1) * metrics.pitch + metrics.continuationTop;
    placements[start].push += target - startTop;
    pushed.add(start);
    shift = shiftAt[start];
    index = start;
  }

  const contentHeight = blockContentHeight + shift;
  const sheetCount = sheetCountFor(contentHeight, metrics);
  return {
    placements,
    contentHeight,
    sheetCount,
    blockHeight: blockHeightFor(sheetCount, metrics),
  };
}
